import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { UserPlus, CheckCircle2, KeyRound } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useTranslation } from "react-i18next";

export default function PatientClaim() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/public/patient-claim/${token}`)
      .then((r) => { setInfo(r.data); setEmail(r.data.patient_email || ""); })
      .catch((e) => setError(apiError(e)));
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (password.length < 6) { toast.error(t("claim.passwordTooShort")); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/public/patient-claim/${token}`, { password, email });
      localStorage.setItem("patient_token", data.token);
      toast.success(t("claim.done"));
      navigate("/mi-portal");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  }

  if (error) return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-6">
      <div className="bg-white border border-stone-200 rounded-2xl p-10 max-w-md text-center">
        <p className="text-sm text-stone-500">{error}</p>
      </div>
    </div>
  );
  if (!info) return <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center text-stone-400">…</div>;
  if (info.already_claimed) return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-6">
      <div className="bg-white border border-stone-200 rounded-2xl p-10 max-w-md text-center space-y-3">
        <CheckCircle2 className="w-10 h-10 text-[#8FA08B] mx-auto" strokeWidth={1.25} />
        <div className="font-heading text-2xl font-light">{t("claim.alreadyDoneTitle")}</div>
        <p className="text-sm text-stone-500">{t("claim.alreadyDoneHint")}</p>
        <button onClick={() => navigate("/portal")} data-testid="go-login"
          className="mt-2 px-6 py-2 rounded-full bg-stone-900 text-white text-xs uppercase tracking-widest">
          {t("claim.goLogin")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-stone-900">
      <header className="border-b border-stone-200 bg-white/70">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="font-heading text-xl font-light">Ambra</div>
          <LanguageSwitcher />
        </div>
      </header>
      <div className="max-w-lg mx-auto px-6 py-16">
        <p className="text-xs uppercase tracking-[0.3em] text-[#B07D4F] mb-3">{t("claim.eyebrow")}</p>
        <h1 className="font-heading text-4xl lg:text-5xl font-light mb-4">{t("claim.title")}</h1>
        <p className="text-stone-600 mb-8">
          {t("claim.subtitle", { name: info.patient_name })} <span className="text-stone-900 font-medium">{info.doctor_name}</span>{info.clinic_name ? ` · ${info.clinic_name}` : ""}.
        </p>
        <form onSubmit={submit} className="bg-white border border-stone-200 rounded-2xl p-6 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5 block">{t("claim.email")}</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="claim-email" placeholder="tuemail@..."
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#8FA08B]" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5 block">{t("claim.newPassword")}</label>
            <div className="flex items-center gap-2 bg-[#FAF9F6] border border-stone-200 rounded-xl px-3">
              <KeyRound className="w-4 h-4 text-stone-400" />
              <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="claim-password"
                className="flex-1 py-3 bg-transparent focus:outline-none" />
            </div>
            <p className="text-[10px] text-stone-400 mt-1">{t("claim.passwordHint")}</p>
          </div>
          <button type="submit" disabled={saving} data-testid="claim-submit"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-stone-900 text-white hover:bg-stone-700 disabled:opacity-50">
            <UserPlus className="w-4 h-4" /> {saving ? "…" : t("claim.cta")}
          </button>
        </form>
      </div>
    </div>
  );
}
